import React, { useState, useEffect, ChangeEvent } from 'react';
import PackageCard from '../components/PackageCard';
import { packageData } from '../utils/packageData';
import { Package, Filters } from '../types';
import { getMinimumListingPrice } from '../utils/pricing';
import '../styling/Packages.css';

const Packages: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<Package[]>([]);
  const [filters, setFilters] = useState<Filters>({
    destination: '',
    priceRange: '',
    duration: '',
    type: ''
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const packagesPerPage = 9;

  useEffect(() => {
    setPackages(packageData);
    setFilteredPackages(packageData);
  }, []);

  useEffect(() => {
    let filtered = packages;

    if (filters.destination) {
      filtered = filtered.filter(pkg => 
        pkg.location.toLowerCase().includes(filters.destination.toLowerCase())
      );
    }

    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(pkg => {
        const { price } = getMinimumListingPrice(pkg.pricing);
        return price >= min && price <= max;
      });
    }

    if (filters.duration) {
      filtered = filtered.filter(pkg => pkg.overview.duration === filters.duration);
    }

    if (filters.type) {
      // Note: You might want to add a 'type' field to your Package interface
      // For now, I'll comment this out as it's not in the current data structure
      // filtered = filtered.filter(pkg => pkg.type === filters.type);
    }

    setFilteredPackages(filtered);
    setCurrentPage(1);
  }, [filters, packages]);

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = (): void => {
    setFilters({
      destination: '',
      priceRange: '',
      duration: '',
      type: ''
    });
  };

  const totalPages = Math.ceil(filteredPackages.length / packagesPerPage);
  const startIndex = (currentPage - 1) * packagesPerPage;
  const endIndex = startIndex + packagesPerPage;
  const currentPackages = filteredPackages.slice(startIndex, endIndex);

  return (
    <div>
      <div className="packages-header">
        <div className="container">
          <h1 className="packages-title">Travel Packages</h1>
          <p className="packages-subtitle">
            Discover amazing destinations and find your perfect adventure
          </p>
        </div>
      </div>

      <div className="container">
        <div className="filters-container">
          <form className="filters-form">
            <div className="filter-group">
              <label className="filter-label">Destination</label>
              <input
                type="text"
                className="filter-input"
                name="destination"
                placeholder="Search destinations..."
                value={filters.destination}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Price Range</label>
              <select
                className="filter-select"
                name="priceRange"
                value={filters.priceRange}
                onChange={handleFilterChange}
              >
                <option value="">All Prices</option>
                <option value="0-500">Under ₹500</option>
                <option value="500-1000">₹500 - ₹1,000</option>
                <option value="1000-2000">₹1,000 - ₹2,000</option>
                <option value="2000-999999">Over ₹2,000</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Duration</label>
              <select
                className="filter-select"
                name="duration"
                value={filters.duration}
                onChange={handleFilterChange}
              >
                <option value="">Any Duration</option>
                <option value="7 Days">7 Days</option>
                <option value="10 Days">10 Days</option>
                <option value="14 Days">14 Days</option>
                <option value="21 Days">21 Days</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Package Type</label>
              <select
                className="filter-select"
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="Beach">Beach</option>
                <option value="Cultural">Cultural</option>
                <option value="Adventure">Adventure</option>
                <option value="Luxury">Luxury</option>
              </select>
            </div>

            <button
              type="button"
              className="filter-button"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </form>
        </div>

        <div className="packages-grid">
          {currentPackages.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            
            <button
              className="pagination-button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Packages; 